import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, FileUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { BookSwitcher } from "@/components/BookSwitcher";
import { BookDialog } from "@/components/BookDialog";
import { FilterBar } from "@/components/FilterBar";
import { TotalsStrip } from "@/components/TotalsStrip";
import { LedgerTable } from "@/components/LedgerTable";
import { EntryDrawer } from "@/components/EntryDrawer";
import { SheetTabs } from "@/components/SheetTabs";
import { LinkedSettlements } from "@/components/LinkedSettlements";
import { ImportDialog } from "@/components/ImportDialog";
import { isZero } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function SheetPage() {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const { can } = useAuth();
  const sheet = search.get("sheet") === "sett" && can("sett") ? "sett" : "daily";
  const [books, setBooks] = useState([]);
  const [data, setData] = useState(null);
  const [range, setRangeState] = useState(() => ({ start: search.get("start") || "", end: search.get("end") || "" }));
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [editBook, setEditBook] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const mismatches = data ? data.rows.filter((r) => !isZero(r.pnl)) : [];
  const jumpNext = () => {
    if (!mismatches.length) return;
    const i = mismatches.findIndex((r) => r.id === activeId);
    const target = mismatches[(i + 1) % mismatches.length];
    setActiveId(target.id);
    document.getElementById(`row-${target.id}`)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  };
  const posLabel = activeId && mismatches.some((r) => r.id === activeId)
    ? `${mismatches.findIndex((r) => r.id === activeId) + 1}/${mismatches.length}` : mismatches.length;

  const load = useCallback(() => {
    const params = { start: range.start || undefined, end: range.end || undefined, sheet };
    return api.get(`/books/${id}/sheet`, { params }).then((r) => { if (r.data.sheet === sheet) setData(r.data); }).catch((e) => toast.error(errMsg(e)));
  }, [id, range, sheet]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/books").then((r) => setBooks(r.data)).catch(() => {}); }, [id]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/books/${id}/export`, { params: { start: range.start || undefined, end: range.end || undefined, sheet }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.book.name.replace(/\s+/g, "_")}${sheet === "sett" ? "_SETT" : ""}_${range.start || "all"}_${range.end || "all"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const openAdd = () => setDrawer({ open: true, row: null });

  return (
    <div className="min-h-screen grid-paper pb-24">
      <AppHeader>
        {data && <BookSwitcher books={books} current={data.book} onRename={() => setEditBook(true)} />}
      </AppHeader>
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-5 space-y-3">
        {!data ? <p className="text-sm text-slate-500" data-testid="sheet-loading">Loading sheet…</p> : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3 fade-up">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{sheet === "sett" ? "Settlement sheet · NEW SETT" : "Daily sheet"}</p>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase" data-testid="sheet-book-name">{data.book.name}</h1>
                <p className="text-xs text-slate-500 mt-1 font-mono" data-testid="sheet-next-info">
                  Next Op Bal {fmt(data.next.op_bal)} · Freeze {fmt(data.next.freeze_bal)}
                </p>
              </div>
              {can("add") && <button data-testid="add-entry-btn" onClick={openAdd}
                className="hidden sm:flex h-11 px-5 bg-slate-900 text-white font-semibold items-center gap-2 hover:bg-slate-800 active:scale-[0.98] transition-[background-color,transform]">
                <Plus size={16} /> Add entry
              </button>}
            </div>
            <SheetTabs sheet={sheet} showSett={can("sett")} onChange={(v) => { setData(null); setSearch(v === "sett" ? { sheet: "sett" } : {}); }} />
            <FilterBar start={range.start} end={range.end} setRange={(start, end) => setRangeState({ start, end })}
              onExport={exportXlsx} exporting={exporting} canExport={can("export")} />
            <div className="flex flex-wrap gap-2">
              <button data-testid="mismatch-jump-btn" onClick={jumpNext} disabled={!mismatches.length}
                className={`h-10 px-4 flex items-center gap-2 text-sm font-semibold border transition-colors ${mismatches.length ? "bg-red-600 text-white border-red-600 hover:bg-red-700" : "bg-green-50 text-green-700 border-green-300"}`}>
                {mismatches.length ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
                {mismatches.length ? <>Next mismatch <span className="font-mono" data-testid="mismatch-jump-count">{posLabel}</span></> : "No mismatches"}
              </button>
              {can("import") && <button data-testid="import-open-btn" onClick={() => setImportOpen(true)}
                className="h-10 px-4 flex items-center gap-2 text-sm font-semibold bg-white border border-slate-300 hover:border-slate-900 transition-colors">
                <FileUp size={15} /> Import Excel
              </button>}
            </div>
            <TotalsStrip t={data.totals} />
            <LedgerTable rows={data.rows} activeId={activeId} onRowClick={(row) => setDrawer({ open: true, row })} />
            {sheet === "sett" && <LinkedSettlements rows={data.linked} onOpen={() => setSearch({})} />}
          </>
        )}
      </main>
      {can("add") && <button data-testid="add-entry-fab" onClick={openAdd} disabled={!data}
        className="sm:hidden fixed bottom-5 right-5 z-30 h-14 px-5 bg-slate-900 text-white font-semibold flex items-center gap-2 shadow-[4px_4px_0_0_#facc15] active:translate-y-0.5 transition-transform">
        <Plus size={18} /> Add entry
      </button>}
      {data && (
        <>
          <EntryDrawer open={drawer.open} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
            row={drawer.row} next={data.next} bookId={id} sheet={sheet} onSaved={load} />
          <ImportDialog open={importOpen} onOpenChange={setImportOpen} bookId={id} sheet={sheet}
            onDone={() => { load(); api.get("/books").then((r) => setBooks(r.data)); }} />
          <BookDialog open={editBook} onOpenChange={setEditBook} book={data.book}
            onSaved={() => { load(); api.get("/books").then((r) => setBooks(r.data)); }} />
        </>
      )}
    </div>
  );
}
